type DatasetInfo = {
  displayName: string;
  shortName: string;
  domoName: string;
  description: string;
  availableToLLM: boolean;
  availableToUser: boolean; // Whether this appears in the dataset selector UI
};

export type DatasetName =
  | "main"
  | "benchmark"
  | "attrition_drivers"
  | "attrition_directional_drivers"
  | "methodology"
  | "recs"
  | "recruitment_applications"
  | "time_to_fill_drivers"
  | "faq"
  | "culture"
  | "employee_relations"
  | "osha_incidents"
  | "employee_goals"
  | "lightcast";

export const DATASETS: Record<DatasetName, DatasetInfo> = {
  main: {
    displayName: "HRIS Data",
    shortName: "HRIS",
    domoName:
      "View [Console]PROD for Query Bar | Stage4|Employee Data|Daily|Viz",
    description:
      "Main dataset containing employee information for one or more companies including demographics, organizational data, compensation, and employment details.",
    availableToLLM: true,
    availableToUser: true,
  },
  recruitment_applications: {
    displayName: "Recruitment",
    shortName: "Recruitment",
    domoName: "View of PROD | Applications",
    description:
      "Dataset containing job application details including applicant demographics, application status, recruiter, recruitment stage, and other relevant information.",
    availableToLLM: true,
    availableToUser: true,
  },
  benchmark: {
    displayName: "Benchmark Data",
    shortName: "Benchmark",
    domoName:
      "[NP] PROD | Stage 5(B) | Benchmarking Data Integrated with Company Representation | Race & Gender",
    description:
      "Benchmarking data for HR leaders to compare their company against industry norms.",
    availableToLLM: true,
    availableToUser: true,
  },
  attrition_drivers: {
    displayName: "Attrition Drivers",
    shortName: "Attrition Drivers",
    domoName: "Console_Top_Drivers_v1",
    description:
      "Dataset containing analysis of factors contributing to employee attrition and their relative impact.",
    availableToLLM: true,
    availableToUser: false, // this is set to false because the attrition_drivers measure is added artificially to the HRIS dataset
  },
  attrition_directional_drivers: {
    displayName: "Directional Drivers",
    shortName: "Directional Drivers",
    domoName: "Attrition Directional Drivers",
    description:
      "Dataset showing factors that increase or decrease attrition, with their directional influence values.",
    availableToLLM: false,
    availableToUser: false,
  },
  methodology: {
    displayName: "Methodology",
    shortName: "Methodology",
    domoName: "View of Methodology",
    description:
      "Dataset detailing the methodologies used in analyses and reports.",
    availableToLLM: false,
    availableToUser: false,
  },
  recs: {
    displayName: "Recommendations",
    shortName: "Recommendations",
    domoName: "Console_Recs_v2",
    description:
      "List of recommended actions for HR leaders to improve their business.",
    availableToLLM: false,
    availableToUser: false,
  },
  time_to_fill_drivers: {
    displayName: "Top Drivers of Longer Time to Fill",
    shortName: "Time to Fill Drivers",
    domoName: "View of PROD | Recruitment Directional Drivers",
    description:
      "Dataset showing factors that increase or decrease time to fill in the recruitment process. Negative impact values indicate a reduction in time to fill, while positive values indicate an increase, i.e. issues with the process.",
    availableToLLM: true,
    availableToUser: false,
  },
  faq: {
    displayName: "FAQ",
    shortName: "FAQ",
    domoName: "View of Console_FAQ",
    description:
      "Frequently Asked Questions dataset containing common questions and answers related to the console and its data.",
    availableToLLM: false,
    availableToUser: false,
  },
  culture: {
    displayName: "Culture",
    shortName: "Culture",
    domoName: "View of ETL Output - Culture",
    description:
      "Dataset containing employee survey results, including employee feedback, cultural assessments, and related metrics.",
    availableToLLM: true,
    availableToUser: true,
  },
  employee_relations: {
    displayName: "Employee Relations",
    shortName: "Employee Relations",
    domoName: "View of Employee Relations",
    description:
      "Dataset containing information related to employee relations, including cases, investigations, and resolutions.",
    availableToLLM: true,
    availableToUser: true,
  },
  osha_incidents: {
    displayName: "OSHA Incidents",
    shortName: "OSHA Incidents",
    domoName: "View of OSHA Incidents",
    description:
      "Dataset containing information related to OSHA incidents, including incident reports, investigations, and compliance data.",
    availableToLLM: true,
    availableToUser: true,
  },
  employee_goals: {
    displayName: "Employee Goals",
    shortName: "Employee Goals",
    domoName: "View of Employee Goals",
    description:
      "Dataset containing employee goals and related performance metrics.",
    availableToLLM: true,
    availableToUser: true,
  },
  lightcast: {
    displayName: "Lightcast",
    shortName: "Lightcast",
    domoName: "View of lightcast_benchmark",
    description:
      "External Lightcast labor-market benchmarks by company-selected location, industry, occupation, and year. Use for questions about local versus national job supply and demand, hires, separations, openings, employment growth, wages and pay premiums, industry concentration, labor availability, and sector unemployment. Do not use for the company's own employees, applicants, vacancies, or internal turnover. Values are modeled market estimates; separation intensity measures exits per job, not resignation probability, and labor availability ratio is not the unemployment rate.",
    availableToLLM: true,
    availableToUser: true,
  },
};

export const LIKERT_SCALE_LABELS: Record<string, string> = {
  "1": "Strongly Disagree",
  "2": "Disagree",
  "3": "Neutral",
  "4": "Agree",
  "5": "Strongly Agree",
};
